/**
 * Design tokens extracted from Figma.
 * These are the primitive values that the semantic theme references.
 */

// ─── Named Primitive Color Scales ────────────────────────────────────

export const violet = {
  25: "#faf8ff",
  50: "#f5f0ff",
  100: "#ebe0ff",
  200: "#d6c0ff",
  300: "#b090ff",
  400: "#9D5FFF",
  500: "#7c4de6",
  600: "#5b3acc",
  700: "#3a1aad",
  800: "#190a8a",
  900: "#000066",
  950: "#000033",
} as const;

export const magenta = {
  25: "#fff8fd",
  50: "#fff0fa",
  100: "#ffe0f5",
  200: "#ffc0eb",
  300: "#ff90d8",
  400: "#FF78F3",
  500: "#e650d4",
  600: "#cc28b5",
  700: "#a3008c",
  800: "#7a0066",
  900: "#520044",
  950: "#290022",
} as const;

export const gray = {
  25: "#fdfcfc",
  50: "#faf9f8",
  100: "#f5f3f1",
  200: "#e8e5e3",
  300: "#d8d8dc",
  400: "#a0a0a6",
  500: "#71717a",
  600: "#52525b",
  700: "#3f3f46",
  800: "#27272a",
  900: "#18181b",
  950: "#09090b",
} as const;

export const red = {
  25: "#fff8f7",
  50: "#fff0ee",
  100: "#ffe0dd",
  200: "#ffc0bb",
  300: "#ff9090",
  400: "#FF2821",
  500: "#e61a1a",
  600: "#cc1010",
  700: "#a30808",
  800: "#7a0000",
  900: "#520000",
  950: "#290000",
} as const;

// ─── Misc Chart Scales ────────────────────────────────────────────────

export const misc = {
  teal: {
    25: "#f6fefc", 50: "#f0fdf9", 100: "#ccfbef", 200: "#99f6e0",
    300: "#5fe9d0", 400: "#2ed3b7", 500: "#15b79e", 600: "#0e9384",
    700: "#107569", 800: "#125d56", 900: "#134e48", 950: "#0a2926",
  },
  orange: {
    25: "#fefaf5", 50: "#fef6ee", 100: "#fdead7", 200: "#f9dbaf",
    300: "#f7b27a", 400: "#f38744", 500: "#ef6820", 600: "#e04f16",
    700: "#b93815", 800: "#932f19", 900: "#772917", 950: "#511c10",
  },
  purple: {
    25: "#fafaff", 50: "#f4f3ff", 100: "#ebe9fe", 200: "#d9d6fe",
    300: "#bdb4fe", 400: "#9b8afb", 500: "#7a5af8", 600: "#6938ef",
    700: "#5925dc", 800: "#4a1fb8", 900: "#3e1c96", 950: "#27115f",
  },
  pink: {
    25: "#fef6fb", 50: "#fdf2fa", 100: "#fce7f6", 200: "#fcceee",
    300: "#faa7e0", 400: "#f670c7", 500: "#ee46bc", 600: "#dd2590",
    700: "#c11574", 800: "#9e165f", 900: "#851651", 950: "#4e0d30",
  },
  fuchsia: {
    25: "#fefaff", 50: "#fdf4ff", 100: "#fbe8ff", 200: "#f6d0fe",
    300: "#eeaafd", 400: "#e478fa", 500: "#d444f1", 600: "#ba24d5",
    700: "#9f1ab1", 800: "#821890", 900: "#6f1877", 950: "#47104c",
  },
  orangeDark: {
    25: "#fff9f5", 50: "#fff4ed", 100: "#ffe6d5", 200: "#ffd6ae",
    300: "#ff9c66", 400: "#ff692e", 500: "#ff4405", 600: "#e62e05",
    700: "#bc1b06", 800: "#97180c", 900: "#771a0d", 950: "#57130a",
  },
  green: {
    25: "#f6fef9", 50: "#edfcf2", 100: "#d3f8df", 200: "#aaf0c4",
    300: "#73e2a3", 400: "#3ccb7f", 500: "#16b364", 600: "#099250",
    700: "#087443", 800: "#095c37", 900: "#084c2e", 950: "#052e1c",
  },
  indigo: {
    25: "#f5f8ff", 50: "#eef4ff", 100: "#e0eaff", 200: "#c7d7fe",
    300: "#a4bcfd", 400: "#8098f9", 500: "#6172f3", 600: "#444ce7",
    700: "#3538cd", 800: "#2d31a6", 900: "#2d3282", 950: "#1f235b",
  },
  rose: {
    25: "#fff5f6", 50: "#fff1f3", 100: "#ffe4e8", 200: "#fecdd6",
    300: "#fea3b4", 400: "#fd6f8e", 500: "#f63d68", 600: "#e31b54",
    700: "#c01048", 800: "#a11043", 900: "#89123e", 950: "#510b24",
  },
  blueLight: {
    25: "#f5fbff", 50: "#f0f9ff", 100: "#e0f2fe", 200: "#b9e6fe",
    300: "#7cd4fd", 400: "#36bffa", 500: "#0ba5ec", 600: "#0086c9",
    700: "#026aa2", 800: "#065986", 900: "#0b4a6f", 950: "#062c41",
  },
  cyan: {
    25: "#f5feff", 50: "#ecfdff", 100: "#cff9fe", 200: "#a5f0fc",
    300: "#67e3f9", 400: "#22ccee", 500: "#06aed4", 600: "#088ab2",
    700: "#0e7090", 800: "#155b75", 900: "#164c63", 950: "#0d2d3a",
  },
} as const;

// ─── Base Colors ────────────────────────────────────────────────────

export const base = {
  white: "#ffffff",
  black: "#000000",
  transparent: "#ffffff00",
} as const;

// ─── Status Scales ─────────────────────────────────────────────────

export const error = {
  25: "#fffbfa",
  50: "#fef3f2",
  100: "#fee4e2",
  200: "#fecdca",
  300: "#fda29b",
  400: "#f97066",
  500: "#f04438",
  600: "#d92d20",
  700: "#b42318",
  800: "#912018",
  900: "#7a271a",
  950: "#55160c",
} as const;

export const warning = {
  25: "#fffcf5",
  50: "#fffaeb",
  100: "#fef0c7",
  200: "#fedf89",
  300: "#fec84b",
  400: "#fdb022",
  500: "#f79009",
  600: "#dc6803",
  700: "#b54708",
  800: "#93370d",
  900: "#7a2e0e",
  950: "#4e1d09",
} as const;

export const success = {
  25: "#f6fef9",
  50: "#ecfdf3",
  100: "#dcfae6",
  200: "#abefc6",
  300: "#75e0a7",
  400: "#47cd89",
  500: "#17b26a",
  600: "#079455",
  700: "#067647",
  800: "#085d3a",
  900: "#074d31",
  950: "#053321",
} as const;

export const info = {
  25: "#f5faff",
  50: "#eff8ff",
  100: "#d1e9ff",
  200: "#b2ddff",
  300: "#84caff",
  400: "#53b1fd",
  500: "#2e90fa",
  600: "#1570ef",
  700: "#175cd3",
  800: "#1849a9",
  900: "#194185",
  950: "#102a56",
} as const;

// ─── Spacing ────────────────────────────────────────────────────────

export const spacing = {
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  "2xl": 20,
  "3xl": 24,
  "4xl": 32,
  "5xl": 40,
  "6xl": 48,
  "7xl": 64,
  "8xl": 80,
  "9xl": 96,
  "10xl": 128,
  "11xl": 160,
} as const;

// ─── Border Radius ──────────────────────────────────────────────────

export const radius = {
  none: 0,
  xxs: 2,
  xs: 4,
  sm: 6,
  md: 8,
  lg: 10,
  xl: 12,
  "2xl": 16,
  "3xl": 20,
  "4xl": 24,
  full: 9999,
} as const;

// ─── Shadows ────────────────────────────────────────────────────────

export const shadows = {
  xs: "0px 1px 2px 0px #1018280d",
  sm: "0px 1px 3px 0px #1018281a, 0px 1px 2px 0px #1018280f",
  md: "0px 4px 8px -2px #1018281a, 0px 2px 4px -2px #1018280f",
  lg: "0px 12px 16px -4px #10182814, 0px 4px 6px -2px #10182808",
  xl: "0px 20px 24px -4px #10182814, 0px 8px 8px -4px #10182808",
  "2xl": "0px 24px 48px -12px #1018282e",
  "3xl": "0px 32px 64px -12px #10182824",
} as const;

// ─── Focus Rings ────────────────────────────────────────────────────

export const focusRings = {
  brand: "0px 0px 0px 4px #7c4de63d",
  brandShadowXs: "0px 1px 2px 0px #1018280d, 0px 0px 0px 4px #7c4de63d",
  brandShadowSm: "0px 1px 3px 0px #1018281a, 0px 1px 2px 0px #1018280f, 0px 0px 0px 4px #7c4de63d",
  gray: "0px 0px 0px 4px #a0a0a624",
  grayShadowXs: "0px 1px 2px 0px #1018280d, 0px 0px 0px 4px #a0a0a624",
  grayShadowSm: "0px 1px 3px 0px #1018281a, 0px 1px 2px 0px #1018280f, 0px 0px 0px 4px #a0a0a624",
  error: "0px 0px 0px 4px #f044383d",
  errorShadowXs: "0px 1px 2px 0px #1018280d, 0px 0px 0px 4px #f044383d",
} as const;

// ─── Typography ─────────────────────────────────────────────────────

export const fontFamily = '"Aeonik", sans-serif';
export const fontFamilyMono = '"Aeonik Mono", monospace';

export const fontSize = {
  xxs: 11,
  xs: 12,
  sm: 14,
  md: 16,
  lg: 18,
  xl: 20,
} as const;

export const lineHeight = {
  xxs: "16px",
  xs: "18px",
  sm: "20px",
  md: "24px",
  lg: "28px",
  xl: "30px",
} as const;

export const fontWeight = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

// ─── Display Typography ─────────────────────────────────────────────

export const display = {
  "2xl": { fontSize: 72, lineHeight: "90px", letterSpacing: "-0.02em" },
  xl: { fontSize: 60, lineHeight: "72px", letterSpacing: "-0.02em" },
  lg: { fontSize: 48, lineHeight: "60px", letterSpacing: "-0.02em" },
  md: { fontSize: 36, lineHeight: "44px", letterSpacing: "-0.02em" },
  sm: { fontSize: 30, lineHeight: "38px" },
  xs: { fontSize: 24, lineHeight: "32px" },
} as const;

// ─── Container ──────────────────────────────────────────────────────

export const container = {
  maxWidthDesktop: 1280,
  paddingDesktop: 32,
  paddingMobile: 16,
} as const;

// ─── Width Breakpoints ──────────────────────────────────────────────

export const widths = {
  xxs: 320,
  xs: 384,
  sm: 480,
  md: 560,
  lg: 640,
  xl: 768,
  "2xl": 1024,
  "3xl": 1280,
  "4xl": 1440,
  "5xl": 1600,
  "6xl": 1920,
} as const;

// ─── Semantic Colors — Text ───────────────────────────────────────

export const text = {
  primary: gray[900],
  secondary: gray[700],
  secondaryHover: gray[800],
  tertiary: gray[600],
  tertiaryHover: gray[700],
  quaternary: gray[500],
  disabled: gray[500],
  placeholder: gray[500],
  placeholderSubtle: gray[300],
  white: base.white,
  brandPrimary: violet[900],
  brandSecondary: violet[700],
  brandTertiary: violet[600],
  brandTertiaryAlt: violet[600],
  primaryOnBrand: base.white,
  secondaryOnBrand: violet[200],
  tertiaryOnBrand: violet[200],
  quaternaryOnBrand: violet[300],
  errorPrimary: error[600],
  warningPrimary: warning[600],
  successPrimary: success[600],
} as const;

// ─── Semantic Colors — Background ─────────────────────────────────

export const bg = {
  primary: gray[50],
  primaryAlt: gray[50],
  primaryHover: gray[100],
  primarySolid: gray[950],
  secondary: base.white,
  secondaryAlt: base.white,
  secondarySubtle: gray[25],
  secondaryHover: gray[50],
  secondarySolid: gray[600],
  tertiary: gray[100],
  quaternary: gray[200],
  active: gray[200],
  disabled: gray[100],
  disabledSubtle: gray[50],
  overlay: gray[950],
  brandPrimary: violet[50],
  brandPrimaryAlt: violet[50],
  brandSecondary: violet[100],
  brandSolid: violet[600],
  brandSolidHover: violet[700],
  brandSection: violet[800],
  brandSectionSubtle: violet[700],
  errorPrimary: error[50],
  errorSecondary: error[100],
  errorSolid: error[600],
  warningPrimary: warning[50],
  warningSecondary: warning[100],
  warningSolid: warning[600],
  successPrimary: success[50],
  successSecondary: success[100],
  successSolid: success[600],
} as const;

// ─── Semantic Colors — Foreground ─────────────────────────────────

export const fg = {
  primary: gray[900],
  secondary: gray[700],
  secondaryHover: gray[800],
  tertiary: gray[600],
  tertiaryHover: gray[700],
  quaternary: gray[500],
  quaternaryHover: gray[600],
  quinary: gray[400],
  quinaryHover: gray[500],
  senary: gray[300],
  white: base.white,
  disabled: gray[400],
  disabledSubtle: gray[300],
  brandPrimary: violet[600],
  brandPrimaryAlt: violet[600],
  brandSecondary: violet[500],
  errorPrimary: error[600],
  errorSecondary: error[500],
  warningPrimary: warning[600],
  warningSecondary: warning[500],
  successPrimary: success[600],
  successSecondary: success[500],
} as const;

// ─── Semantic Colors — Border ─────────────────────────────────────

export const border = {
  primary: gray[300],
  secondary: gray[200],
  tertiary: gray[100],
  disabled: gray[300],
  disabledSubtle: gray[200],
  brand: violet[300],
  brandSolid: violet[600],
  brandSolidAlt: violet[600],
  error: error[300],
  errorSolid: error[600],
} as const;

// ─── Component Colors — Buttons ───────────────────────────────────

export const buttonColors = {
  primary: {
    bg: violet[600],
    bgHover: violet[700],
    fg: base.white,
    fgHover: base.white,
    border: violet[600],
    borderHover: violet[700],
  },
  secondary: {
    bg: gray[900],
    bgHover: gray[950],
    fg: base.white,
    fgHover: base.white,
    border: gray[900],
    borderHover: gray[950],
  },
  secondaryColor: {
    bg: base.white,
    bgHover: violet[50],
    fg: violet[700],
    fgHover: violet[800],
    border: violet[300],
    borderHover: violet[300],
  },
  tertiary: {
    fg: gray[600],
    fgHover: gray[700],
    bgHover: gray[50],
  },
  tertiaryColor: {
    fg: violet[700],
    fgHover: violet[800],
    bgHover: violet[50],
  },
  primaryError: {
    bg: error[600],
    bgHover: error[700],
    fg: base.white,
    fgHover: base.white,
    border: error[600],
    borderHover: error[700],
  },
  secondaryError: {
    bg: base.white,
    bgHover: error[50],
    fg: error[700],
    fgHover: error[800],
    border: error[300],
    borderHover: error[300],
  },
  tertiaryError: {
    fg: error[700],
    fgHover: error[800],
    bgHover: error[50],
  },
} as const;

// ─── Component Colors — Avatars ───────────────────────────────────

export const avatarColors = {
  bg: gray[100],
  contrastBorder: "#00000014",
  profilePhotoBorder: base.white,
  focusBorder: "#a0a0a624",
} as const;

// ─── Component Colors — Breadcrumbs ───────────────────────────────

export const breadcrumbColors = {
  fg: gray[600],
  fgHover: gray[700],
  bgHover: gray[50],
  brandBgHover: violet[50],
  brandFgHover: violet[700],
  iconFg: gray[500],
  iconFgHover: gray[700],
  brandIconFgHover: violet[700],
} as const;

// ─── Component Colors — Icons ─────────────────────────────────────

export const iconColors = {
  fgBrand: violet[600],
  fgBrandOnBrand: violet[200],
  featuredModernBorder: gray[200],
  featuredLightFgBrand: violet[600],
  featuredLightFgGray: gray[500],
  featuredLightFgError: error[600],
  featuredLightFgWarning: warning[600],
  featuredLightFgSuccess: success[600],
  featuredDarkFgBrand: base.white,
  featuredDarkFgGray: base.white,
  featuredDarkFgError: base.white,
  featuredDarkFgWarning: base.white,
  featuredDarkFgSuccess: base.white,
} as const;

// ─── Component Colors — Navigation ───────────────────────────────

export const navColors = {
  itemIconFg: gray[500],
  itemIconFgActive: gray[700],
  itemButtonIconFg: gray[500],
  itemButtonIconFgActive: gray[700],
} as const;

// ─── Component Colors — Sliders ───────────────────────────────────

export const sliderColors = {
  handleBorder: violet[600],
  handleBg: base.white,
} as const;

// ─── Component Colors — Toggles ───────────────────────────────────

export const toggleColors = {
  buttonFgDisabled: gray[50],
} as const;

// ─── Component Colors — Charts ────────────────────────────────────

export const chartColors = {
  axisFg: gray[100],
  gridlineFg: gray[100],
  labelFg: gray[600],
  labelFgEmphasis: gray[700],
  bg: base.white,
  bgAlt: gray[50],
  tooltipBg: gray[950],
  tooltipFg: base.white,
  crosshairFg: gray[500],
  legendFg: text.tertiary,
  series: [
    { fg: misc.purple[500], fgHover: misc.purple[600], bg: misc.purple[100], bgHover: misc.purple[200] },
    { fg: misc.orangeDark[400], fgHover: misc.orangeDark[500], bg: misc.orangeDark[100], bgHover: misc.orangeDark[200] },
    { fg: misc.teal[300], fgHover: misc.teal[400], bg: misc.teal[50], bgHover: misc.teal[100] },
    { fg: warning[400], fgHover: warning[500], bg: warning[100], bgHover: warning[200] },
    { fg: misc.rose[400], fgHover: misc.rose[500], bg: misc.rose[100], bgHover: misc.rose[200] },
    { fg: misc.blueLight[400], fgHover: misc.blueLight[500], bg: misc.blueLight[100], bgHover: misc.blueLight[200] },
    { fg: misc.orange[300], fgHover: misc.orange[400], bg: misc.orange[100], bgHover: misc.orange[200] },
    { fg: misc.cyan[600], fgHover: misc.cyan[700], bg: misc.cyan[100], bgHover: misc.cyan[200] },
    { fg: misc.green[500], fgHover: misc.green[600], bg: misc.green[100], bgHover: misc.green[200] },
    { fg: misc.pink[400], fgHover: misc.pink[500], bg: misc.pink[100], bgHover: misc.pink[200] },
    { fg: misc.fuchsia[400], fgHover: misc.fuchsia[500], bg: misc.fuchsia[100], bgHover: misc.fuchsia[200] },
    { fg: misc.teal[600], fgHover: misc.teal[700], bg: misc.teal[100], bgHover: misc.teal[200] },
  ],
} as const;

// ─── Alpha Colors ─────────────────────────────────────────────────

export const alpha = {
  white: {
    10: "#ffffff1a",
    20: "#ffffff33",
    30: "#ffffff4d",
    40: "#ffffff66",
    50: "#ffffff80",
    60: "#ffffff99",
    70: "#ffffffb3",
    80: "#ffffffcc",
    90: "#ffffffe6",
    100: "#ffffff",
  },
  black: {
    10: "#0000001a",
    20: "#00000033",
    30: "#0000004d",
    40: "#00000066",
    50: "#00000080",
    60: "#00000099",
    70: "#000000b3",
    80: "#000000cc",
    90: "#000000e6",
    100: "#000000",
  },
} as const;
